import React from 'react';

import {
  CHARGED_ON_CONFIRM_CHIP_LABEL,
  CHARGED_TODAY_LABEL,
  QUOTE_TO_FOLLOW_LABEL,
} from '../../constants/chargeTimingCopy';
import { FILLS, FONTS, RADII, STROKES } from '../../styles/tokens';
import type { ChargeTiming } from '../forms/ChargeTimingConsent';

export interface ChargeTimingChipProps {
  timing: ChargeTiming;
}

const chipLabel = (timing: ChargeTiming): string =>
  timing === 'ON_QUOTE'
    ? QUOTE_TO_FOLLOW_LABEL
    : timing === 'TODAY'
      ? CHARGED_TODAY_LABEL
      : CHARGED_ON_CONFIRM_CHIP_LABEL;

const chipStyle = (timing: ChargeTiming): React.CSSProperties => {
  const isToday = timing === 'TODAY';
  return {
    // inline-flex + nowrap keeps the label on one line and its caps
    // centred in the pill; narrow columns otherwise wrap "Quote to follow".
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    whiteSpace: 'nowrap',
    lineHeight: 1,
    marginTop: '6px',
    fontFamily: FONTS.body,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '5px 10px',
    borderRadius: RADII.pill,
    color: isToday ? FILLS.success : FILLS.gold,
    background: isToday ? FILLS.successBgStrong : FILLS.goldAlpha10,
    border: `1px solid ${isToday ? STROKES.success : STROKES.goldCard}`,
  };
};

/**
 * The per-line charge-timing pill shown on a checkout/cart line: "Charged
 * today" for a NextPax stay, "On confirm" for a native line, "Quote to
 * follow" for a by-request line. Single source of truth for this label and
 * style, shared by `CheckoutSummaryRail`, `CartPage` and `CartDropdown` so
 * the three surfaces can never render three different chips for the same
 * `ChargeTiming`.
 */
const ChargeTimingChip: React.FC<ChargeTimingChipProps> = ({ timing }) => (
  <span style={chipStyle(timing)}>{chipLabel(timing)}</span>
);

export default ChargeTimingChip;
